import React from "react";
import { Input, Radio, Switch } from "antd";

import { lineByKey as L } from "../../TreatmentPlans/lib/procedureFamilies";
import { Field, YesNo } from "./fields";

/**
 * Step 4 — hygiene &amp; recall: the block the front desk reads every single day.
 *
 * It sits high on their paper form for that reason, and the recall cycle governs
 * the whole hygiene family — fluoride, bitewings, polishing and recall exams all
 * key off it.
 *
 * Two patterns borrowed straight from the sheet:
 *   - the CONDITIONAL "is perio scaling different from preventative scaling?",
 *     which only asks for a second percentage when the answer is yes;
 *   - a split recall cycle by age, which their notes call out as a thing people
 *     forget to record ("*6MR under 18Y. 9MR over 18Y").
 */
export default function HygieneStep({ value, onChange }) {
  function set(k, v) { onChange(Object.assign({}, value, { [k]: v })); }
  return (
    <div className="bw-card">
      <p className="bw-card-title">Hygiene &amp; recall</p>
      <div className="bw-grid">
        <Field label="Recall cycle" hint="Drives fluoride, bitewings, polishing and recall exams.">
          <Input suffix="months" value={value.recallCycle || ""} onChange={function (e) { set("recallCycle", e.target.value); }} />
        </Field>
        <Field label="Scaling units allowed">
          <Input value={value.scalingUnits || ""} onChange={function (e) { set("scalingUnits", e.target.value); }} />
        </Field>
        <Field label="Counted per">
          <Radio.Group size="small" optionType="button" buttonStyle="solid"
            value={value.scalingBasis || ""}
            onChange={function (e) { set("scalingBasis", e.target.value); }}
            options={[
              { label: "Year", value: "Year" },
              { label: "12 consecutive months", value: "12 Consecutive Months" },
              { label: "Apart", value: "Apart" },
            ]} />
        </Field>
      </div>

      <div className="bw-conditional">
        <Field label="Preventative scaling" code={L.preventativeScaling.templateCode}>
          <Input suffix="%" style={{ width: 140 }} value={value.prevScalingPct || ""}
            onChange={function (e) { set("prevScalingPct", e.target.value); }} />
        </Field>
        <div style={{ marginTop: 12 }}>
          <Switch size="small" checked={!!value.perioScalingDiffers}
            onChange={function (v) { set("perioScalingDiffers", v); }} />
          <span style={{ marginLeft: 10, fontSize: 13, color: "#3d2f5e" }}>
            Perio scaling is covered differently from preventative scaling
          </span>
        </div>
        {value.perioScalingDiffers ? (
          <div style={{ marginTop: 12 }}>
            <Field label="Perio scaling / root planing" code={L.perioScaling.templateCode}>
              <Input suffix="%" style={{ width: 140 }} value={value.perioScalingPct || ""}
                onChange={function (e) { set("perioScalingPct", e.target.value); }} />
            </Field>
          </div>
        ) : null}
      </div>

      <div className="bw-conditional" style={{ marginTop: 14 }}>
        <div style={{ fontSize: 13, color: "#3d2f5e", marginBottom: 10 }}>
          Does the recall cycle differ by age?
        </div>
        <div className="bw-inline">
          <Input style={{ width: 150 }} placeholder="Children (months)" value={value.recallCycleChildren || ""}
            onChange={function (e) { set("recallCycleChildren", e.target.value); }} />
          <span style={{ color: "#a99ec4", fontSize: 13 }}>under age</span>
          <Input style={{ width: 100 }} placeholder="18" value={value.recallSplitAge || ""}
            onChange={function (e) { set("recallSplitAge", e.target.value); }} />
        </div>
        <p className="bw-hint">Recorded as an exception: “*6MR under 18Y. 9MR over 18Y”.</p>
      </div>

      <div className="bw-grid" style={{ marginTop: 18 }}>
        <Field label="Oral exam interval" code={L.exam.templateCode}>
          <Input suffix="months" value={value.oeMonths || ""} onChange={function (e) { set("oeMonths", e.target.value); }} />
        </Field>
        <Field label="Panoramic interval" code={L.pan.templateCode}>
          <Input suffix="months" value={value.panMonths || ""} onChange={function (e) { set("panMonths", e.target.value); }} />
        </Field>
        <Field label="Bitewing interval" code={L.bitewings.templateCode}>
          <Input suffix="months" value={value.bwMonths || ""} onChange={function (e) { set("bwMonths", e.target.value); }} />
        </Field>
        <Field label="Perio surgery" code={L.perioSurgery.templateCode}>
          <Input suffix="%" value={value.perioSurgeryPct || ""} onChange={function (e) { set("perioSurgeryPct", e.target.value); }} />
        </Field>
        <Field label="Sealants" code={L.sealants.templateCode}>
          <YesNo value={value.sealants || ""} onChange={function (v) { set("sealants", v); }} />
        </Field>
        <Field label="Composites on molars" code={L.molarComposites.templateCode}>
          <YesNo value={value.compositesMolars || ""} onChange={function (v) { set("compositesMolars", v); }} />
        </Field>
        <Field label="Oral hygiene instruction" code={L.ohi.templateCode}>
          <YesNo value={value.ohi || ""} onChange={function (v) { set("ohi", v); }} />
        </Field>
        <Field label="Reline covered" code={L.reline.templateCode}>
          <Input suffix="%" value={value.relinePct || ""} onChange={function (e) { set("relinePct", e.target.value); }} />
        </Field>
        <Field label="Fluoride" code={L.fluoride.templateCode}>
          <div className="bw-inline">
            <YesNo value={value.fluoride || ""} onChange={function (v) { set("fluoride", v); }} />
            <Input style={{ width: 130 }} placeholder="age limit" value={value.fluorideAgeLimit || ""}
              onChange={function (e) { set("fluorideAgeLimit", e.target.value); }} />
          </div>
        </Field>
      </div>
    </div>
  );
}
